import axios from "axios";
import { API_BASE_URL } from "./advancedSettings";

export const MAX_TERMS = 500;
export const MAX_TERM_LENGTH = 100;

export interface Glossary {
  terms: string[];
  /** UTC ISO time of the last save, or null when the stream has no glossary yet. */
  updatedAt: string | null;
}

/** One term per line (commas also separate, so a pasted list works). Trims, drops blanks and case-insensitive duplicates. */
export function parseTerms(text: string): string[] {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const raw of text.split(/[\n,]/)) {
    const term = raw.trim().replace(/\s+/g, " ");
    const key = term.toLowerCase();
    if (!term || seen.has(key)) continue;
    seen.add(key);
    terms.push(term);
  }
  return terms;
}

/** Returns a message when the terms cannot be saved, otherwise null. */
export function validateTerms(terms: string[]): string | null {
  if (terms.length > MAX_TERMS) return `A glossary can hold up to ${MAX_TERMS} terms. You have ${terms.length}.`;
  const tooLong = terms.find((t) => t.length > MAX_TERM_LENGTH);
  if (tooLong) return `Each term can be up to ${MAX_TERM_LENGTH} characters: “${tooLong.slice(0, 30)}…” is too long.`;
  return null;
}

export async function fetchGlossary(streamName: string, uid: string, get: typeof axios.get = axios.get): Promise<Glossary> {
  const { data } = await get(`${API_BASE_URL}/glossary/GetGlossaryForStream`, { params: { streamName, uid } });
  const terms: string[] = Array.isArray(data?.terms) ? data.terms : [];
  // The API answers with "now" for a stream that has no glossary, so an empty list means "never saved".
  return { terms, updatedAt: terms.length > 0 && data?.updatedAt ? data.updatedAt : null };
}

export async function saveGlossary(streamName: string, uid: string, terms: string[], post: typeof axios.post = axios.post): Promise<void> {
  await post(`${API_BASE_URL}/glossary/CreateOrUpdateGlossaryForStream`, { streamName, uid, terms });
}
